import axios from "axios";
import { Metadata, ProblemsDictionary } from "../types/Metadata";
import {
  Answer,
  EditableReportQuestion,
  FollowUpLetterDto,
  FollowUpLetterFile,
  FormTemplate,
  GetReportsBetweenDatesResult,
  LetterDto,
  ParticipantAnswer,
  ProblemAnswer,
  ProblemType,
  QuestionDescriptionDTO,
  RaportSection,
  Report,
  ReportFile,
  ReportState,
  ReviewComment,
  TemplateEditableSection,
} from "../types/Rows";
import { ApiURI } from "../types/configs";
import { headers, headersCorrelated } from "./headers";
import { StudyInstance } from "./studyApi";
import { QuestionsInstance } from "./questionsApi";
import { ReportActionDto } from "../types/reportActions";

export const ReportsInstance = axios.create({
  baseURL: ApiURI + "Reports/",
});

export const TemplatesInstance = axios.create({
  baseURL: ApiURI + "Templates/",
});

export const VisitsInstance = axios.create({
  baseURL: ApiURI + "Visits/",
});


export const fetchReports = async () => {
  let result = await ReportsInstance.get<Report[]>("", headers());
  return result.data;
};

export const fetchReportsByState = async (state: ReportState) => {
  let result = await ReportsInstance.get<Report[]>(
    "status/" + state,
    headers()
  );
  return result.data;
};

export interface ProblemReportItem {
  id: string;
  category: string;
  problemType: 0 | 1; // 0=MAJOR, 1=MINOR
  description: string;
  participantNumber: string | null;
  author: string;
  identificationDate: string | null;
  resolvedDate: string | null;
  lastEdited: string;
  reportName: string;
  resortName: string;
  studyName: string;
  reportId: string;
  reportState: number; // 0=INPROGRESS, 1=TO_VERIFY, 2=TO_CORRECT, 3=ACCEPTED
}

export const fetchAllProblems = async (): Promise<ProblemReportItem[]> => {
  const result = await ReportsInstance.get<ProblemReportItem[]>("problems", headers());
  return result.data;
};

export const fetchReportsBetweenDates = async (
  startDate: Date,
  endDate: Date
) => {
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const result = await ReportsInstance.get<GetReportsBetweenDatesResult[]>(
    `between-dates?${params.toString()}`,
    headers()
  );
  return result.data;
};

export const exportReportsBetweenDatesToExcel = async (
  startDate: Date,
  endDate: Date
) => {
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const header = headers().headers;
  const result = await ReportsInstance.get(
    `between-dates/export-excel?${params.toString()}`,
    {
      responseType: 'blob',
      headers: {
        Authorization: header.Authorization,
      },
    }
  );
  return result.data;
};

export const fetchReport = async (id: string) => {
  return (await ReportsInstance.get<Report>(id, headers())).data;
};

export const fetchMetadata = async (id: string) => {
  return (await ReportsInstance.get<Metadata>(id + "/Metadata/", headers()))
    .data;
};

export const fetchProblems = async (id: string) => {
  return (
    await ReportsInstance.get<ProblemAnswer[]>(id + "/problems/", headers())
  ).data;
};

export const fetchProblemsMinor = async (id: string) => {
  return (
    await ReportsInstance.get<ProblemAnswer[]>(
      id + "/problems/minor",
      headers()
    )
  ).data;
};

export const fetchConfirmationLetter = async (id: string) => {
  return (
    await ReportsInstance.post<LetterDto>(
      id + "/confirmationletter/",
      {},
      headers()
    )
  ).data;
};

export const updateConfirmationLetter = async (
  id: string,
  letter: LetterDto
) => {
  return (
    await ReportsInstance.put<LetterDto>(
      id + "/confirmationletter/",
      letter,
      headers()
    )
  ).data;
};

export const fetchFollowUpLetter = async (id: string) => {
  return (await ReportsInstance.get(`${id}/followupletter/`, headers())).data;
};

export const updateFollowUpLetter = async (
  id: string,
  letter: FollowUpLetterDto
) => {
  return (await ReportsInstance.put(`${id}/followupletter/`, letter, headers()))
    .data;
};

export const deleteProblem = async (reportId: string, id: string) => {
  return (
    await ReportsInstance.delete<any>(reportId + "/problems/" + id, headers())
  ).data;
};

export const fetchSections = async (id: string) => {
  return (
    await ReportsInstance.get<RaportSection[]>(id + "/questions/", headers())
  ).data;
};

export const updateReportAnswers = async (
  reportId: string,
  answers: Answer[],
  correlationId: string
) => {
  return await ReportsInstance.post<any>(
    reportId + "/answers/",
    answers,
    headersCorrelated(correlationId)
  );
};

export const copyAnswersFromPreviousReport = async (
  reportId: string,
  otherReportId: string
) => {
  return await ReportsInstance.put<any>(
    reportId + "/reports/" + otherReportId,
    {},
    headers()
  );
};

export const updateMetadata = async (raportId: string, metadata: Metadata, correlationId: string) => {
  return await ReportsInstance.put<any>(
    `${raportId}/metadata`,
    metadata,
    headersCorrelated(correlationId)
  );
};

export const fetchTemplate = async (id: string) => {
  return (await TemplatesInstance.get<FormTemplate>(id, headers())).data;
};

export const deleteTemplate = async (id: string) => {
  console.log("deleting template id", id);
  return await TemplatesInstance.delete<any>(id, headers());
};

export const updateTemplateName = async (id: string, name: string) => {
  // nagłówki jako config (wcześniej trafiały do treści żądania, więc szło bez tokenu i kończyło się 401)
  return await TemplatesInstance.patch<any>(id + "/name/" + encodeURIComponent(name), {}, headers());
};

export const toggleTemplate = async (id: string) => {
  return await TemplatesInstance.put<any>(id + "/locked", {}, headers());
};

export const deleteSection = async (templateId: string, id: string) => {
  return await TemplatesInstance.delete<any>(
    templateId + "/sections/" + id,
    headers()
  );
};
export const deleteReport = async (id: string) => {
  return await ReportsInstance.delete<any>(id, headers());
};

export const getParticipants = async (id: string) => {
  return (
    await ReportsInstance.get<ParticipantAnswer[]>(
      id + "/participants",
      headers()
    )
  ).data;
};

export const getReportFiles = async (id: string) => {
  return (await ReportsInstance.get<ReportFile[]>(id + "/file", headers()))
    .data;
};

export const GetReportFile = async (reportId: string, id: string) => {
  return (
    await ReportsInstance.get(id + "/file/" + id, {
      responseType: "arraybuffer", // Ensure responseType is 'arraybuffer'
      ...headers(),
    })
  ).data;
};

export const UploadFile = async (reportId: string, file: any, name: string) => {
  let header = headers().headers;

  return await ReportsInstance.post(reportId + "/file/" + name, file, {
    headers: {
      Authorization: header.Authorization,
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteFile = async (reportId: string, fileId: string) => {
  let header = headers().headers;

  return await ReportsInstance.delete(reportId + "/file/" + fileId, {
    headers: {
      Authorization: header.Authorization,
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getFulFiles = async (reportId: string) => {
  return (await ReportsInstance.get<FollowUpLetterFile[]>(reportId + "/followupletter/files/", headers())).data;
};

export const uploadFulFile = async (reportId: string, formData: FormData, name: string) => {
  const header = headers().headers;
  return (await ReportsInstance.post<FollowUpLetterFile>(reportId + "/followupletter/files/" + name, formData, {
    headers: { Authorization: header.Authorization, "Content-Type": "multipart/form-data" },
  })).data;
};

export const getFulFileBytes = async (reportId: string, fileId: string): Promise<ArrayBuffer> => {
  const response = await ReportsInstance.get(
    reportId + "/followupletter/files/" + fileId + "/download",
    { responseType: "arraybuffer", ...headers() }
  );
  return response.data as ArrayBuffer;
};

export const downloadFulFile = async (reportId: string, fileId: string, fileName: string) => {
  const response = await ReportsInstance.get(
    reportId + "/followupletter/files/" + fileId + "/download",
    { responseType: "blob", ...headers() }
  );
  const url = URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const toggleFulFileInclude = async (reportId: string, fileId: string, includeInPdf: boolean) => {
  return await ReportsInstance.put(reportId + "/followupletter/files/" + fileId + "/include", includeInPdf, headers());
};

export const deleteFulFile = async (reportId: string, fileId: string) => {
  return await ReportsInstance.delete(reportId + "/followupletter/files/" + fileId, headers());
};

export const updateReportFileLabel = async (reportId: string, fileId: string, label: string) => {
  const header = headers().headers;
  return await ReportsInstance.patch(
    `${reportId}/file/${fileId}/label`,
    label ? JSON.stringify(label) : null,
    { headers: { Authorization: header.Authorization, "Content-Type": "application/json" } }
  );
};

export const updateFulFileLabel = async (reportId: string, fileId: string, label: string) => {
  const header = headers().headers;
  return await ReportsInstance.patch(
    `${reportId}/followupletter/files/${fileId}/label`,
    label ? JSON.stringify(label) : null,
    { headers: { Authorization: header.Authorization, "Content-Type": "application/json" } }
  );
};

export const addParticipant = async (id: string, participantNumber: string) => {
  return await ReportsInstance.post<any>(
    id + "/participants/" + participantNumber,
    {},
    headers()
  );
};

export const removeParticipant = async (
  id: string,
  participantNumber: string
) => {
  return await ReportsInstance.delete<any>(
    id + "/participants/" + participantNumber,
    headers()
  );
};

export const updateReportState = async (id: string, state: ReportState) => {
  return await ReportsInstance.patch<any>(
    id + "/state/" + state,
    {},
    headers()
  );
};

export const updateReportName = async (reportId: string, newName: string) => {
  return await ReportsInstance.patch(
    `${reportId}/${encodeURIComponent(newName)}`,
    {},
    headers()
  );
};

// Typ i numer wizyty z nagłówka raportu; nazwa raportu = typ + numer (jedno źródło numeru).
// duplicates: inne raporty tego szablonu w ośrodku z tym samym numerem (tylko ostrzeżenie)
export const updateVisit = async (reportId: string, visitName: string, visitNumber: number | null) => {
  const res = await ReportsInstance.put<{ name: string; duplicates: string[] }>(
    `${reportId}/visit`,
    { visitName, visitNumber },
    headers()
  );
  return res.data;
};

export const updateVisitName = async (reportId: string, visitName: string) => {
  return await ReportsInstance.patch(
    `${reportId}/visit-name/${encodeURIComponent(visitName)}`,
    {},
    headers()
  );
};

export const PUTparticipants = async (
  id: string,
  participants: ParticipantAnswer[],
  correlationId
) => {
  return await ReportsInstance.put<any>(
    id + "/participants",
    participants,
    headersCorrelated(correlationId)
  );
};

// OBSOLETE
export const fetchAllQuestions = async () => {
  return (
    await TemplatesInstance.get<EditableReportQuestion[]>(
      "questions/",
      headers()
    )
  ).data;
};

export const fetchAllDescriptions = async () => {
  return (
    await QuestionsInstance.get<QuestionDescriptionDTO[]>(
      "descriptions",
      headers()
    )
  ).data;
};

export const fetchTemplateQuestions = async (id: string) => {
  return (
    await TemplatesInstance.get<TemplateEditableSection[]>(
      id + "/questions/",
      headers()
    )
  ).data;
};

export const assignQuestionsFromTemplate = async (
  templateId: string,
  sourceId: string
) => {
  return await TemplatesInstance.put<any>(
    templateId + "/questions/templates/" + sourceId,
    {},
    headers()
  );
};

export const updateSections = async (
  templateId: string,
  sections: TemplateEditableSection[]
) => {
  return await TemplatesInstance.put<any>(
    templateId + "/sections/",
    sections,
    headers()
  );
};

export const getAllAvailableTemplates = async (studyId: string) => {
  return await StudyInstance.get<FormTemplate[]>(
    studyId + "/templates",
    headers()
  );
};

export const createFromExistingTemplate = async (
  studyId: string,
  originTemplateId: string
) => {
  return await TemplatesInstance.post<any>(
    "",
    {
      studyId: studyId,
      originTemplateId: originTemplateId,
    },
    headers()
  );
};

export const getComments = async (reportId: string) => {
  return await ReportsInstance.get<ReviewComment[]>(
    reportId + "/comments/",
    headers()
  );
};

export const addComment = async (
  reportId: string,
  parentId: string,
  val: string
) => {
  return await ReportsInstance.post<any>(
    reportId + "/comments/" + parentId,
    { value: val },
    headers()
  );
};

export const updateCommentState = async (
  reportId: string,
  id: string,
  state: number
) => {
  return await ReportsInstance.patch<any>(
    reportId + "/comments/" + id + "/" + state,
    {},
    headers()
  );
};

export interface AddQuestionDto {
  questionId?: string;
  descriptionId: string;
  weight: number;
  categoryId?: number;
  requiredAnswerToBeVisible?: string;
  sectionId: string;
  isBlank: boolean;
  parentId?: string;
}

export const addQuestion = async (
  templateId: string,
  sectionId: string,
  question: any
) => {
  return await ReportsInstance.post<any>(
    "templates/" +
      templateId +
      "/sections/" +
      sectionId +
      "/questions/" +
      question.descriptionId,
    question,
    headers()
  );
};

export const addQuestionWithDto = async (
  templateId: string,
  sectionId: string,
  question: AddQuestionDto
) => {
  return await ReportsInstance.post<any>(
    "templates/"+templateId + "/sections/" + sectionId + "/questions",
    question,
    headers()
  );
};

export const updateTemplateProblemAnswers = async (
  reportId: string,
  answers: ProblemAnswer[],
  correlationId: string
) => {
  return await ReportsInstance.post<any>(
    reportId + "/answers/problems/",
    answers,
    headersCorrelated(correlationId)
  );
};

export const getTemplate = async (id: string) => {
  return (await TemplatesInstance.get<FormTemplate>(id, headers())).data;
};

export const getTemplates = async (studyId) => {
  return (
    await StudyInstance.get<FormTemplate[]>(studyId + "/templates", headers())
  ).data;
};

// Obsolete - to be removed
export const getAllTemplates = async () => {
  return (await TemplatesInstance.get<FormTemplate[]>("", headers())).data;
};

// TODO - use in scope of reports
export const updateTemplateProblemQuestions = async (
  reportId: string,
  answers: ProblemAnswer[]
) => {
  return await ReportsInstance.put<any>(
    reportId + "/questions/problems",
    answers,
    headers()
  );
};

export const addProblemFromExisting = async (
  reportId: string,
  value: string,
  problemType: ProblemType
) => {
  let url = reportId + "/questions/problems/";
  if (problemType == ProblemType.MINOR) {
    url += "minor";
  }

  return await ReportsInstance.post<any>(url, { value: value }, headers());
};

export const addNewProblem = async (reportId: string, value: string) => {
  return await ReportsInstance.post<any>(
    reportId + "/questions/ProblemType/Category/",
    { value: value },
    headers()
  );
};

export const updateQuestions = async (
  templateId: string,
  questions: EditableReportQuestion[]
) => {
  return await TemplatesInstance.put<any>(
    templateId + "/questions",
    questions,
    headers()
  );
};

export const deleteQuestion = async (
  templateId: string,
  questionsId: string
) => {
  return await TemplatesInstance.delete<any>(
    templateId + "/questions/" + questionsId,
    headers()
  );
};

export const updateFootnote = async (templateId: string, value: string) => {
  return await TemplatesInstance.patch<any>(
    templateId + "/footnote/",
    { value: value },
    headers()
  );
};

export const updateFootnoteFul = async (templateId: string, value: string) => {
  return await TemplatesInstance.patch<any>(
    templateId + "/footnoteful/",
    { value: value },
    headers()
  );
};

export const fetchReportActions = async (reportId: string) => {
  return (
    await ReportsInstance.get<ReportActionDto[]>(
      `${reportId}/actions`,
      headers()
    )
  ).data;
};

export interface StateTransitionDto {
  date: string;
  changedBy: string;
  fromState: number | null;
  toState: number | null;
}

export const fetchReportStateHistory = async (reportId: string): Promise<StateTransitionDto[]> =>
  (await ReportsInstance.get<StateTransitionDto[]>(`${reportId}/state-history`, headers())).data;

export interface UpdateReportTrackingDatesDto {
  sentToSponsorDate: string | null;
  tmfDate: string | null;
}

export const updateReportTrackingDates = async (
  reportId: string,
  dates: UpdateReportTrackingDatesDto
) => {
  return await ReportsInstance.patch(`${reportId}/tracking-dates`, dates, headers());
};

export interface UpdateCmTrackingDatesDto {
  cmAnalysisDate: string | null;
  cmReportDate: string | null;
  cmPmVerificationDate: string | null;
  sentToSponsorDate: string | null;
  tmfDate: string | null;
}

export const updateCmTrackingDates = async (
  reportId: string,
  dates: UpdateCmTrackingDatesDto
) => {
  return await ReportsInstance.patch(`${reportId}/cm-tracking-dates`, dates, headers());
};



export const deleteReportAction = async (
  reportId: string,
  actionId: string,
) => {
  return (
    await ReportsInstance.delete(
      `${reportId}/actions/${actionId}`,
      headers()
    )
  ).data;
};

export const updateReportAction = async (
  reportId: string,
  actionId: string,
  action: any
) => {
  return (
    await ReportsInstance.put<ReportActionDto>(
      `${reportId}/actions/${actionId}`,
      action,
      headers()
    )
  ).data;
};

export const addReportAction = async (
  reportId: string,
  action: Partial<ReportActionDto>
) => {
  return (
    await ReportsInstance.post<ReportActionDto>(
      `${reportId}/actions`,
       action,
      headers()
    )
  ).data;
};

export interface AddPlannedReportDto {
  templateId: string;
  studyId: string;
  resortId: string;
  dateStart: string; // ISO date string
  dateEnd?: string; // ISO date string
  name?: string;
  isRemote:boolean;
  participants?: string;
}

export interface UpdatePlannedReportDto {
  id: string;
  templateId: string;
  studyId: string;
  resortId: string;
  dateStart: string;
  dateEnd?: string;
  name?: string;
  participants?: string;
  isRemote: boolean;
  reportId?: string;
}

export interface PlannedReport {
  id: string;
  reportId?: string;
  templateId?: string;
  name?: string;
  studyId?: string;
  studyName?: string;
  resortId?: string;
  resortName?: string;
  dateStart: string;
  dateEnd?: string;
  participants?: string;
  created: string;
  createdBy: string;
}

export enum PlannedReportState {
  NEW = 0,
}

export interface PlannedVisit {
  id: string;
  reportId?: string;
  templateId?: string;
  name?: string;
  isRemote?: boolean;
  studyId?: string;
  studyName?: string;
  resortId?: string;
  resortName?: string;
  dateStart: string;
  dateEnd?: string;
  participants?: string;
  comment?: string;
  created: string;
  createdBy: string;
}

// FROM HERE

export const addPlannedReport = async (data: AddPlannedReportDto) => {
  console.log('data is', data);
  return await VisitsInstance.post<any>(
    "planned",
    data,
    headers()
  );
};

export const updatePlannedReport = async (id: string, data: UpdatePlannedReportDto) => {
  return await VisitsInstance.put<any>(
    `planned/${id}`,
    data,
    headers()
  );
};

export const deletePlannedReport = async (id: string) => {
  return await VisitsInstance.delete<any>(
    `planned/${id}`,
    headers()
  );
};

export const joinPlannedVisit = async (id: string) => {
  return await VisitsInstance.post<any>(
    `planned/${id}/join`,
    {},
    headers()
  );
};



export const getVisit = async (id: string) => {
  return (await VisitsInstance.get<PlannedVisit>(
    `${id}`,
    headers()
  )).data;
};

// Raporty z ośrodka wizyty do połączenia z nią; sugerowane (nazwa lub data) na górze.
export interface PlannedVisitReportCandidate {
  id: string;
  name: string;
  visitName?: string | null;
  visitNumber?: number | null;
  visitDate?: string | null;
  state: ReportState;
  templateName?: string | null;
  linkedVisitId?: string | null;
  linkedVisitName?: string | null;
  suggested: boolean;
}

export const fetchVisitReportCandidates = async (visitId: string) => {
  return (await VisitsInstance.get<PlannedVisitReportCandidate[]>(
    `planned/${visitId}/report-candidates`,
    headers()
  )).data;
};

// reportId = null odłącza raport od wizyty; nie wysyła zaproszeń (w przeciwieństwie do updatePlannedReport)
export const linkPlannedVisitReport = async (visitId: string, reportId: string | null) => {
  return await VisitsInstance.put<void>(
    `planned/${visitId}/report`,
    { reportId },
    headers()
  );
};

export const fetchPlannedReports = async (
  startDate: Date,
  endDate: Date
) => {
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const result = await VisitsInstance.get<PlannedReport[]>(
    `planned?${params.toString()}`,
    headers()
  );
  return result.data;
};

export const exportPlannedVisitsToExcel = async (
  startDate: Date,
  endDate: Date
) => {
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const header = headers().headers;
  const result = await VisitsInstance.get(
    `planned/export-excel?${params.toString()}`,
    {
      responseType: 'blob',
      headers: {
        Authorization: header.Authorization,
      },
    }
  );
  return result.data;
};

export const fetchFuturePlannedVisits = async (studyId: string) => {
  return (
    await VisitsInstance.get<PlannedVisit[]>(
      `planned/studies/${studyId}/future`,
      headers()
    )
  ).data;
};

export const fetchAllFuturePlannedVisits = async (): Promise<PlannedVisit[]> => {
  // Od początku dnia: wizyty zapisywane są z godziną 00:00, więc "teraz" wycinałoby dzisiejsze
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date();
  endDate.setFullYear(endDate.getFullYear() + 2);
  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  const result = await VisitsInstance.get<PlannedVisit[]>(
    `planned?${params.toString()}`,
    headers()
  );
  return result.data;
};

// Import rejestru akcji z .xlsx (SR-014): backend zwraca komórki jako tekst, walidacja we froncie
export type SpreadsheetTable = { sheetName: string; sheetCount: number; firstRow: number; rows: string[][] };
export const parseActionLogSpreadsheet = async (reportId: string, file: File) => {
  const header = headers().headers;
  const form = new FormData();
  form.append("file", file);
  return (
    await ReportsInstance.post<SpreadsheetTable>(`${reportId}/actions/import/parse`, form, {
      headers: { Authorization: header.Authorization, "Content-Type": "multipart/form-data" },
    })
  ).data;
};

// Wzór rejestru akcji (.xlsx z listami rozwijanymi); withData = rejestr raportu (eksport)
export const downloadActionLogTemplate = async (reportId: string, withData: boolean) => {
  const header = headers().headers;
  return (
    await ReportsInstance.get<Blob>(`${reportId}/actions/template?withData=${withData}`, {
      responseType: "blob",
      headers: { Authorization: header.Authorization },
    })
  ).data;
};

// Historia wygenerowanych listów (SR-035)
export type GeneratedDocumentType = "CL" | "FUL";
export type GeneratedDocument = { id: string; documentType: GeneratedDocumentType; fileName: string; sizeBytes: number; created: string; createdBy: string };

export const fetchGeneratedDocuments = async (reportId: string) =>
  (await ReportsInstance.get<GeneratedDocument[]>(`${reportId}/documents`, headers())).data;

export const storeGeneratedDocument = async (reportId: string, type: GeneratedDocumentType, pdf: Blob, fileName: string) => {
  const header = headers().headers;
  const form = new FormData();
  form.append("file", pdf, fileName);
  return (
    await ReportsInstance.post<GeneratedDocument>(`${reportId}/documents/${type}`, form, {
      headers: { Authorization: header.Authorization, "Content-Type": "multipart/form-data" },
    })
  ).data;
};

export const downloadGeneratedDocument = async (reportId: string, id: string) =>
  (
    await ReportsInstance.get<Blob>(`${reportId}/documents/${id}/download`, {
      responseType: "blob",
      headers: { Authorization: headers().headers.Authorization },
    })
  ).data;
